import { FORM_CLASSES, FORM_SELECTORS, MODAL_CLASSES } from "./const.js";

/**
 * Класс для управления отображением ответов формы (success/fail)
 * @class FormResponse
 * @description Класс для управления отображением ответов формы, работает совместно с FormModal
 *
 * @property {HTMLElement} container - Контейнер с формой
 * @property {HTMLElement} form - Элемент формы
 * @property {HTMLElement} responseFail - Элемент сообщения об ошибке
 * @property {HTMLElement} responseSuccess - Элемент сообщения об успехе
 * @property {FormModal} formModal - Ссылка на экземпляр FormModal для управления состоянием
 * @property {HTMLElement} responseSuccessButton - Кнопка "Понятно" в сообщении об успехе
 * @property {HTMLElement} responseFailButton - Кнопка "Понятно" в сообщении об ошибке
 *
 * @method init - Инициализирует обработчики событий формы
 * @method addEventListeners - Добавляет обработчики событий
 * @method showResponseSuccess - Показывает сообщение об успехе
 * @method showResponseFail - Показывает сообщение об ошибке
 * @method hideForm - Скрывает форму
 * @method showForm - Показывает форму
 */
export default class FormResponse {
  /**
   * Создает экземпляр FormResponse
   * @param {HTMLElement} container - Контейнер с формой
   * @param {HTMLElement} responseSuccess - Элемент сообщения об успехе
   * @param {HTMLElement} responseFail - Элемент сообщения об ошибке (опционально)
   * @param {FormModal} formModal - Ссылка на экземпляр FormModal
   */
  constructor(container, responseSuccess, responseFail, formModal) {
    this.container = container;
    this.form = container.querySelector(FORM_SELECTORS.FORM);
    this.responseFail = responseFail || null;
    this.responseSuccess = responseSuccess;
    this.formModal = formModal;
    this.responseSuccessButton = this.responseSuccess?.querySelector(
      `.${FORM_CLASSES.RESPONSE_BUTTON}`,
    );
    this.responseFailButton = this.responseFail?.querySelector(
      `.${FORM_CLASSES.RESPONSE_BUTTON}`,
    );
    this.formValidator = null;
  }

  /**
   * Инициализирует обработчики событий формы
   * @returns {void}
   */
  init() {
    if (
      !this.form ||
      !this.responseSuccess ||
      !this.formModal
    ) {
      console.error("Form, responseSuccess or formModal wasn't found");

      return;
    }

    this.addEventListeners();
  }

  /**
   * Добавляет обработчики событий для формы и кнопки "Понятно"
   * @returns {void}
   */
  addEventListeners() {
    // Обработчики событий Contact Form 7
    this.form.addEventListener("wpcf7mailsent", () =>
      this.showResponseSuccess(),
    );

    // Обработчик ошибки только если responseFail существует
    if (this.responseFail) {
      this.form.addEventListener("wpcf7mailfailed", () =>
        this.showResponseFail(),
      );
    }

    // Закрытие response окна по кнопке "Понятно" в success окне
    if (this.responseSuccessButton) {
      this.responseSuccessButton.addEventListener("click", () => {
        this.formModal.handleClose();
      });
    }

    // Закрытие response окна по кнопке "Понятно" в fail окне
    if (this.responseFailButton) {
      this.responseFailButton.addEventListener("click", () => {
        this.formModal.handleClose();
      });
    }

    // Закрытие response окна по клику на иконку закрытия
    const responseCloseIcons = document.querySelectorAll(
      ".response .response__close-icon",
    );
    responseCloseIcons.forEach((icon) => {
      icon.addEventListener("click", () => {
        this.formModal.handleClose();
      });
    });
  }

  /**
   * Устанавливает ссылку на FormValidator для управления состоянием кнопки
   * @param {FormValidator} formValidator - Экземпляр FormValidator
   * @returns {void}
   */
  setFormValidator(formValidator) {
    this.formValidator = formValidator;
  }

  /**
   * Показывает сообщение об успехе и скрывает форму
   * @returns {void}
   */
  showResponseSuccess() {
    this.hideForm();
    this.responseSuccess.classList.add(FORM_CLASSES.RESPONSE_ACTIVE);
    // Включаем кнопку при показе успешного ответа
    if (this.formValidator) {
      this.formValidator.enableSubmitButton();
    }
  }

  /**
   * Показывает сообщение об ошибке и скрывает форму
   * @returns {void}
   */
  showResponseFail() {
    if (!this.responseFail) {
      return;
    }

    this.hideForm();
    this.responseFail.classList.add(FORM_CLASSES.RESPONSE_ACTIVE);
    // Включаем кнопку при показе ошибки
    if (this.formValidator) {
      this.formValidator.enableSubmitButton();
    }
  }

  /**
   * Скрывает форму убирая активный класс модального окна
   * @returns {void}
   */
  hideForm() {
    this.container.classList.remove(MODAL_CLASSES.FORM.ACTIVE);
  }

  /**
   * Показывает форму добавляя активный класс модального окна
   * @returns {void}
   */
  showForm() {
    this.container.classList.add(MODAL_CLASSES.FORM.ACTIVE);
  }
}
